// ARTIST CNTRL PRO — Buses & Trucks hooks (ART-147), hand-written over the generated client via the
// tenant-keyed wrappers. Keys are ['band', bandId, 'buses', …]. Two reads with different audiences:
//   • useFleet   — the whole chart + crew rail; AV-or-TM only (a 403 is the editor gate, surfaced as an upsell
//                  wrapper on the screen — never on a widget).
//   • useMyBunk  — the caller's OWN bunk; any Pro crew member. The four My Bunk surfaces read this.
// Every mutation invalidates ['band', bandId, 'buses'] so the fleet and my-bunk both refresh.
//
// The generated schema marks every field optional (the C# records are non-null, but the OpenAPI doc does not
// say `required`), so the READS are normalised at the boundary into clean non-optional shapes — the screen
// then reads real strings/numbers, not `T | undefined`. Writes send the generated request types as-is.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type RawFleet = components["schemas"]["FleetView"];
type RawVehicle = components["schemas"]["VehicleView"];
type RawMyBunk = components["schemas"]["MyBunkView"];
type RawStock = components["schemas"]["StockView"];
type CreateVehicleRequest = components["schemas"]["CreateVehicleRequest"];
type UpdateVehicleRequest = components["schemas"]["UpdateVehicleRequest"];
type PlaceBunkRequest = components["schemas"]["PlaceBunkRequest"];
type AutoFillResult = components["schemas"]["AutoFillResult"];

// ---- clean view types (non-optional) ---------------------------------------------------------------------

export interface Bunk { slot: number; code: string; junk: boolean; personId: string | null }
export interface BunkLocation { vehicleId: string; vehicleName: string; code: string }
export interface CrewMember {
  personId: string; displayName: string; titleKey: string; party: string;
  avatarMediaId: string | null; assigned: BunkLocation | null;
}
export interface Vehicle {
  vehicleId: string; kind: string; no: number; name: string; position: number; bunkCount: number;
  driver: string | null; driverPhone: string | null; door: string | null; bay: string | null;
  wifi: string | null; wifiPassword: string | null; lockNote: string | null;
  trailer: string | null; cargo: string | null;
  version: number; tag: string | null; filled: number; capacity: number; bunks: Bunk[];
}
export interface FleetStats { vehicles: number; buses: number; trucks: number; bunkTotal: number; placed: number; crewUnassigned: number }
export interface Fleet { vehicles: Vehicle[]; crew: CrewMember[]; stats: FleetStats }
/** The caller's coach, and their sleeper on it. `code`/`long` are null for an UNBUNKED rider on the band's
 *  only coach (the server hands that coach over so they can still get on the bus — ART-148). */
export interface MyBunk {
  vehicleId: string; vehicleName: string; no: number; code: string | null; long: string | null;
  driver: string | null; driverPhone: string | null; door: string | null; bay: string | null;
  wifi: string | null; wifiPassword: string | null; lockNote: string | null;
}
/** `hasBunk` ⇒ `bunk` is your sleeper. `!hasBunk` with a `bunk` ⇒ you ride the band's only coach with no
 *  sleeper yet. `bunk` null with `coaches > 1` ⇒ nothing says which coach is yours ("ask your TM").
 *  `coaches === 0` ⇒ no bus at all. `canEditFleet` is the SERVED AV-or-TM verdict (the fleet pager + the
 *  mobile editor key on it, never on a role the client guesses). */
export interface MyBunkInfo { hasBunk: boolean; bunk: MyBunk | null; coaches: number; canEditFleet: boolean }

export interface StockAsk {
  askId: string; vehicleId: string; vehicleName: string; personId: string;
  personName: string; item: string; qty: number; mine: boolean;
}

// Aliases the screen imports (it reads Fleet["crew"], Vehicle, etc.).
export type { Fleet as FleetView, Vehicle as VehicleView, UpdateVehicleRequest };

function normVehicle(v: RawVehicle): Vehicle {
  return {
    vehicleId: v.vehicleId ?? "", kind: v.kind ?? "bus", no: v.no ?? 0, name: v.name ?? "",
    position: v.position ?? 0, bunkCount: v.bunkCount ?? 0,
    driver: v.driver ?? null, driverPhone: v.driverPhone ?? null, door: v.door ?? null, bay: v.bay ?? null,
    wifi: v.wifi ?? null, wifiPassword: v.wifiPassword ?? null, lockNote: v.lockNote ?? null,
    trailer: v.trailer ?? null, cargo: v.cargo ?? null,
    version: v.version ?? 0, tag: v.tag ?? null, filled: v.filled ?? 0, capacity: v.capacity ?? 0,
    bunks: (v.bunks ?? []).map((b) => ({
      slot: b.slot ?? 0, code: b.code ?? "", junk: b.junk ?? false, personId: b.personId ?? null,
    })),
  };
}
function normFleet(f: RawFleet): Fleet {
  return {
    vehicles: (f.vehicles ?? []).map(normVehicle),
    crew: (f.crew ?? []).map((c) => ({
      personId: c.personId ?? "", displayName: c.displayName ?? "Crew", titleKey: c.titleKey ?? "",
      party: c.party ?? "c", avatarMediaId: c.avatarMediaId ?? null,
      assigned: c.assigned
        ? { vehicleId: c.assigned.vehicleId ?? "", vehicleName: c.assigned.vehicleName ?? "", code: c.assigned.code ?? "" }
        : null,
    })),
    stats: {
      vehicles: f.stats?.vehicles ?? 0, buses: f.stats?.buses ?? 0, trucks: f.stats?.trucks ?? 0,
      bunkTotal: f.stats?.bunkTotal ?? 0, placed: f.stats?.placed ?? 0, crewUnassigned: f.stats?.crewUnassigned ?? 0,
    },
  };
}
function normMyBunk(m: RawMyBunk): MyBunkInfo {
  const b = m.bunk;
  return {
    hasBunk: m.hasBunk ?? false,
    coaches: m.coaches ?? 0,
    canEditFleet: m.canEditFleet ?? false,
    bunk: b ? {
      vehicleId: b.vehicleId ?? "", vehicleName: b.vehicleName ?? "", no: b.no ?? 0,
      code: b.code ?? null, long: b.long ?? null,
      driver: b.driver ?? null, driverPhone: b.driverPhone ?? null, door: b.door ?? null, bay: b.bay ?? null,
      wifi: b.wifi ?? null, wifiPassword: b.wifiPassword ?? null, lockNote: b.lockNote ?? null,
    } : null,
  };
}

// ---- reads -----------------------------------------------------------------------------------------------

export function useFleet(enabled = true) {
  const { api } = useApp();
  return useBandQuery<Fleet>(
    ["buses", "fleet"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/buses", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return normFleet(data.data ?? {});
    },
    { staleTime: 15_000, enabled },
  );
}

export function useMyBunk(enabled = true) {
  const { api } = useApp();
  return useBandQuery<MyBunkInfo>(
    ["buses", "my-bunk"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/buses/my-bunk", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return normMyBunk(data.data ?? {});
    },
    { staleTime: 30_000, enabled },
  );
}

function normStock(s: RawStock): StockAsk[] {
  return (s.asks ?? []).map((a) => ({
    askId: a.askId ?? "", vehicleId: a.vehicleId ?? "", vehicleName: a.vehicleName ?? "",
    personId: a.personId ?? "", personName: a.personName ?? "Crew", item: a.item ?? "",
    qty: a.qty ?? 1, mine: a.mine ?? false,
  }));
}

/** Every restock ask across the fleet (§7.6). The crew panel filters to one coach; the TM view rolls up
 *  identical items. Any Pro band seat. */
export function useStock(enabled = true) {
  const { api } = useApp();
  return useBandQuery<StockAsk[]>(
    ["buses", "stock"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/buses/stock", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return normStock(data.data ?? {});
    },
    { staleTime: 15_000, enabled },
  );
}

export function useAddStock() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, { vehicleId: string; item: string; qty: number }>(
    async (bandId, body) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/buses/stock", { params: { path: { bandId } }, body });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useRetractStock() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, string>(
    async (bandId, askId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/buses/stock/{askId}", { params: { path: { bandId, askId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

function useInvalidateBuses() {
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return () => qc.invalidateQueries({ queryKey: ["band", bandId, "buses"] });
}

// ---- mutations (returns not consumed — normalisation unneeded) --------------------------------------------

export function useAddVehicle() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, CreateVehicleRequest>(
    async (bandId, body) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/buses", { params: { path: { bandId } }, body });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useUpdateVehicle() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, { vehicleId: string; version: number; body: UpdateVehicleRequest }>(
    async (bandId, { vehicleId, version, body }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/buses/{vehicleId}", {
        params: { path: { bandId, vehicleId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useRemoveVehicle() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, string>(
    async (bandId, vehicleId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/buses/{vehicleId}", { params: { path: { bandId, vehicleId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useReorderVehicles() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, string[]>(
    async (bandId, vehicleIds) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/buses/order", { params: { path: { bandId } }, body: { vehicleIds } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function usePlaceBunk() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, PlaceBunkRequest>(
    async (bandId, body) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/buses/place", { params: { path: { bandId } }, body });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useClearBunk() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, { vehicleId: string; slot: number }>(
    async (bandId, { vehicleId, slot }) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/buses/{vehicleId}/bunks/{slot}", { params: { path: { bandId, vehicleId, slot } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useSetBunkJunk() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, { vehicleId: string; slot: number; junk: boolean }>(
    async (bandId, { vehicleId, slot, junk }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/buses/{vehicleId}/bunks/{slot}/junk", { params: { path: { bandId, vehicleId, slot } }, body: { junk } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useClearBus() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<void, string>(
    async (bandId, vehicleId) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/buses/{vehicleId}/clear", { params: { path: { bandId, vehicleId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => invalidate() },
  );
}

export function useAutoFill() {
  const { api } = useApp();
  const invalidate = useInvalidateBuses();
  return useBandMutation<AutoFillResult, { vehicleId: string; party: string | null }>(
    async (bandId, { vehicleId, party }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/buses/{vehicleId}/auto-fill", { params: { path: { bandId, vehicleId } }, body: { party } });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.data ?? {};
    },
    { onSuccess: () => invalidate() },
  );
}
